import { motion } from 'framer-motion';
import { ArrowDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SocialLinks from '@/components/ui/SocialLinks';
import TypingText from '@/components/ui/TypingText';

const RESUME_PDF = '/files/JenaroVegaBecerrilResumePublic2026.pdf';
const RESUME_FILENAME = 'JenaroVegaBecerrilResumePublic2026.pdf';

const Hero = () => {
  const scrollToProjects = () => {
    const projectsSection = document.getElementById('projects');
    if (projectsSection) {
      projectsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const socialLinks = [
    { platform: 'github', url: 'https://github.com/Jenaro98', label: 'GitHub Profile' },
    { platform: 'linkedin', url: 'https://linkedin.com/in/jenaro-vega', label: 'LinkedIn Profile' },
    { platform: 'youtube', url: 'https://youtube.com/@jenarovega7623', label: 'YouTube Channel' },
  ] as const;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delay: 0.2,
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <section className="min-h-[calc(100vh-80px)] pt-32 pb-16 flex items-center relative">
      <div className="container">
        <motion.div
          className="max-w-3xl"
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          <motion.div variants={itemVariants}>
            <h3 className="text-primary text-lg font-medium mb-3">Hello, I&apos;m an</h3>
          </motion.div>

          <motion.div variants={itemVariants}>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Embedded
              <br />
              <span className="text-primary">
                <TypingText text="Software Engineer" delay={800} speed={70} />
              </span>
            </h1>
          </motion.div>

          <motion.div variants={itemVariants}>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl leading-relaxed">
            I build software across the stack, from embedded firmware to web and mobile applications. 
            I enjoy solving practical problems with clean, efficient code.
            </p>
          </motion.div>

          <motion.div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3" variants={itemVariants}>
            <Button size="lg" onClick={scrollToProjects}>
              View my work
            </Button>
            <Button size="lg" variant="outline" asChild>
              <a href={RESUME_PDF} download={RESUME_FILENAME}>
                Download resume
              </a>
            </Button>
            <Button size="lg" variant="outline" onClick={scrollToContact}>
              Get in touch
            </Button>
          </motion.div>

          <motion.div className="mt-12" variants={itemVariants}>
            <SocialLinks links={socialLinks} />
          </motion.div>
        </motion.div>
      </div>

      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce hidden md:block">
        <Button variant="ghost" size="icon" onClick={scrollToProjects} aria-label="Scroll to projects">
          <ArrowDown className="h-5 w-5" />
        </Button>
      </div>
    </section>
  );
};

export default Hero;
